import { useEffect, useMemo } from 'react';

import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { useIsLoggedIn } from '@/hooks/auth';
import { useSettings } from '@/hooks/state/useSettings';
import {
  getApiV1Whitelist,
  getGetApiV1WhitelistQueryKey,
  useGetApiV1Whitelist,
} from '@/open-api/whitelist/whitelist';
import { logEvent } from '@/pages/api/v1/utils';
import { authKeys } from '@/utils/queryKeys';

// Each failed fetch is up to 3 requests (retry: 2). After this many with
// nothing loaded, the query stays off until the user changes, a mutation
// settles or the page reloads, so a persistent 401 can't loop (#2949).
const MAX_FAILED_FETCHES = 3;

// Logged per request rather than per observer: a dozen components mount this
// hook at once.
const fetchWhitelist = async (signal?: AbortSignal) => {
  try {
    return await getApiV1Whitelist(signal);
  } catch (error) {
    if (!signal?.aborted) {
      const code = isAxiosError(error)
        ? (error.response?.status ?? error.code)
        : undefined;

      void logEvent({
        event_type: 'safelist_fetch_failed',
        asPath: window.location.pathname + window.location.search,
        metadata: { code: String(code ?? 'unknown') },
      });
    }

    throw error;
  }
};

// Module-level to survive component remounts — a useRef would reset on each
// mount, causing removeQueries to fire every remount and resetting the failed
// fetch count, which reintroduces the infinite 401 loop. Only mutated inside
// useEffect (client-only), so safe in SSR where useEffect does not execute.
let lastUserId: string | number | undefined;

// Test-only reset to prevent state leaking between test cases
export const __resetLastUserId = () => {
  lastUserId = undefined;
};

export const useWhitelistedChannels = () => {
  const isLoggedIn = useIsLoggedIn();
  const { settings } = useSettings();
  const queryClient = useQueryClient();
  // Note: uses auto-generated key for consistency with useWhitelistChannel and
  // useRemoveChannel mutation hooks which invalidate using the same key.
  const queryKey = useMemo(() => getGetApiV1WhitelistQueryKey(), []);
  const currentUserId = settings?.user.id;

  // Clear cached error state when user changes (re-authentication)
  useEffect(() => {
    if (currentUserId !== lastUserId) {
      lastUserId = currentUserId;
      if (currentUserId) {
        queryClient.removeQueries({ queryKey });
      }
    }
  }, [currentUserId, queryClient, queryKey]);

  // gcTime: Infinity keeps this state in the cache through component remounts
  const cachedQuery = queryClient.getQueryState(queryKey);
  const hasErrored = cachedQuery?.status === 'error';

  // The likeliest failure is a 401 on an expired access token, so an errored
  // query is held while a refresh is in flight. Re-enabling refetches it,
  // which makes every later refresh another attempt. The first fetch is not
  // held: a disabled query isn't loading, so consumers would show their empty
  // state instead of a skeleton.
  const isRefreshingToken = useIsFetching({ queryKey: authKeys.tokens() }) > 0;
  const isWaitingForToken = hasErrored && isRefreshingToken;

  // errorUpdateCount is never reset by a success, so only give up with no
  // data. That's the state the #2949 loop needs, and a loaded list that
  // failed to refresh should keep trying.
  const hasGivenUp =
    hasErrored &&
    cachedQuery.data === undefined &&
    cachedQuery.errorUpdateCount >= MAX_FAILED_FETCHES;

  const shouldEnable =
    isLoggedIn && !!currentUserId && !isWaitingForToken && !hasGivenUp;

  const query = useGetApiV1Whitelist({
    query: {
      enabled: shouldEnable,
      queryFn: ({ signal }) => fetchWhitelist(signal),
      staleTime: Number.POSITIVE_INFINITY,
      gcTime: Number.POSITIVE_INFINITY,
      retry: 2,
      // Without this every newly mounted consumer refetches an errored query,
      // which is the loop from #2949
      retryOnMount: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      select: (data) =>
        data?.channels?.map((channel) => ({
          id: channel?.id,
          channelId: channel?.channelId ?? '',
          channelName: channel?.channelName ?? '',
          endDate: channel?.endDate ?? undefined,
        })) ?? [],
    },
  });

  return {
    ...query,
    data: query.data ?? [],
  };
};
